import { useEffect } from 'react';
import Lightbox, { ILightBoxProps } from 'react-image-lightbox';
import 'react-image-lightbox/style.css';
// @mui
import { useTheme, alpha } from '@mui/material/styles';
import { Typography, GlobalStyles } from '@mui/material';

// ----------------------------------------------------------------------

function LightboxModalStyles() {
  const theme = useTheme();

  const isRTL = theme.direction === 'rtl';

  const ICON_SIZE = 32;

  const ICON_COLOR = theme.palette.grey[600].replace('#', '');

  const getIcon = (icon: string) =>
    `url(https://api.iconify.design/carbon/${icon}.svg?color=%23${ICON_COLOR}&width=${ICON_SIZE}&height=${ICON_SIZE})`;

  const Icon = (icon: string) => ({
    opacity: 1,
    alignItems: 'center',
    display: 'inline-flex',
    justifyContent: 'center',
    backgroundImage: `unset`,
    backgroundColor: 'transparent',
    transition: theme.transitions.create('opacity'),
    '&:before': {
      display: 'block',
      width: ICON_SIZE,
      height: ICON_SIZE,
      content: getIcon(icon),
    },
    '&:hover': {
      opacity: 0.72,
    },
  });

  return (
    <GlobalStyles
      styles={{
        '& .ReactModalPortal': {
          '& .ril__outer': {
            backgroundColor: alpha(theme.palette.grey[900], 0.96),
          },

          // Toolbar
          '& .ril__toolbar': {
            height: 'auto !important',
            padding: theme.spacing(2, 3),
            backgroundColor: 'transparent',
          },
          '& .ril__toolbarLeftSide': { display: 'none' },
          '& .ril__toolbarRightSide': {
            height: 'auto !important',
            padding: 0,
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            '& li': {
              display: 'flex',
              alignItems: 'center',
            },
            '& li:first-of-type': {
              flexGrow: 1,
            },
            '& li:not(:first-of-type)': {
              width: 40,
              height: 40,
              justifyContent: 'center',
              marginLeft: theme.spacing(2),
            },
          },

          // Button
          '& button:focus': { outline: 'none' },
          '& .ril__toolbarRightSide button': {
            width: '100%',
            height: '100%',
            '&.ril__zoomInButton': Icon('zoom-in'),
            '&.ril__zoomOutButton': Icon('zoom-out'),
            '&.ril__closeButton': Icon('close'),
          },
          '& .ril__navButtons': {
            padding: theme.spacing(3),
            '&.ril__navButtonPrev': {
              right: 'auto',
              left: theme.spacing(2),
              ...Icon(isRTL ? 'arrow-right' : 'arrow-left'),
            },
            '&.ril__navButtonNext': {
              left: 'auto',
              right: theme.spacing(2),
              ...Icon(isRTL ? 'arrow-left' : 'arrow-right'),
            },
          },
        },
      }}
    />
  );
}

// ----------------------------------------------------------------------

interface Props extends ILightBoxProps {
  images: string[];
  photoIndex: number;
  setPhotoIndex: (index: number) => void;
  isOpen: boolean;
}

export default function LightboxModal({
  images,
  photoIndex,
  setPhotoIndex,
  isOpen,
  mainSrc,
  ...other
}: Props) {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
  }, [isOpen]);

  const imageCount = images.length;
  const normalizedPhotoIndex = imageCount
    ? Math.min(Math.max(photoIndex, 0), imageCount - 1)
    : 0;
  const hasMultipleImages = imageCount > 1;
  const previousPhotoIndex = imageCount
    ? (normalizedPhotoIndex + imageCount - 1) % imageCount
    : 0;
  const nextPhotoIndex = imageCount
    ? (normalizedPhotoIndex + 1) % imageCount
    : 0;
  const activeMainSrc = mainSrc || images[normalizedPhotoIndex] || '';

  const showIndex = (
    <Typography variant="subtitle2">{`${
      normalizedPhotoIndex + 1
    } / ${imageCount}`}</Typography>
  );

  const toolbarButtons = [showIndex];

  const customStyles = {
    overlay: {
      zIndex: 9999,
    },
  };

  return (
    <>
      <LightboxModalStyles />

      {isOpen && activeMainSrc && (
        <Lightbox
          animationDuration={160}
          mainSrc={activeMainSrc}
          nextSrc={hasMultipleImages ? images[nextPhotoIndex] : undefined}
          prevSrc={hasMultipleImages ? images[previousPhotoIndex] : undefined}
          onMovePrevRequest={
            hasMultipleImages
              ? () => setPhotoIndex(previousPhotoIndex)
              : undefined
          }
          onMoveNextRequest={
            hasMultipleImages ? () => setPhotoIndex(nextPhotoIndex) : undefined
          }
          toolbarButtons={toolbarButtons}
          reactModalStyle={customStyles}
          {...other}
        />
      )}
    </>
  );
}
